import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { api, handleApiError } from '../lib/api';
import Logo from './common/Logo';

interface RegisterErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  password?: string;
  confirmPassword?: string;
  agreeToTerms?: string;
  general?: string;
}

const WellsiteRegister = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<RegisterErrors>({});
  const [successMessage, setSuccessMessage] = useState('');

  const validateForm = () => {
    const newErrors: RegisterErrors = {};

    // First name validation
    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (firstName.length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    // Last name validation
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (lastName.length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    // Email validation
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    // Company validation
    if (!company.trim()) {
      newErrors.company = 'Company is required';
    }

    // Password validation
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms agreement validation
    if (!agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the Terms of Service and Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await api.auth.register({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        company: company.trim(),
        phone: phone.trim() || undefined,
      });
      // Store user data and token
      if (data.data?.user) {
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }
      if (data.data?.token) {
        localStorage.setItem('token', data.data.token);
      }

      setSuccessMessage('Account created successfully! Redirecting...');

      setTimeout(() => {
        navigate('/feed', { replace: true });
      }, 1500);

    } catch (err: any) {
      console.error('Registration error:', err);
      const errorMessage = handleApiError(err);

      if (err.data?.errors && Array.isArray(err.data.errors)) {
        const errorMap: Partial<RegisterErrors> = {};
        err.data.errors.forEach((errorItem: any) => {
          errorMap[errorItem.path as keyof RegisterErrors] = errorItem.msg || errorItem.message;
        });
        setErrors(errorMap as RegisterErrors);
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const renderError = (field: keyof RegisterErrors) => {
    if (errors[field]) {
      return (
        <div className="flex items-center space-x-1 mt-1">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-sm text-red-400">{errors[field]}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #ffffff 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, #ffffff 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Header */}
      <div className="relative z-10 p-6">
        <div className="flex items-center justify-between">
          <Logo className="h-6 w-auto" />
          <div className="text-sm text-gray-400">
            Already have an account?{' '}
            <button
              onClick={handleLoginRedirect}
              className="text-white font-medium hover:underline transition-colors"
            >
              Login
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-6">
              Create a new account
            </h2>
            {successMessage && (
              <div className="flex items-center justify-center space-x-2 bg-green-900/50 border border-green-700 rounded-lg p-3 mb-4">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400">{successMessage}</span>
              </div>
            )}
            {errors.general && (
              <div className="flex items-center justify-center space-x-2 bg-red-900/50 border border-red-700 rounded-lg p-3 mb-4">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400">{errors.general}</span>
              </div>
            )}
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={`w-full px-4 py-3 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                      errors.firstName ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder=""
                    required
                    disabled={isLoading}
                  />
                  {renderError('firstName')}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={`w-full px-4 py-3 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                      errors.lastName ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder=""
                    required
                    disabled={isLoading}
                  />
                  {renderError('lastName')}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Company *
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className={`w-full px-4 py-3 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                    errors.company ? 'border-red-500' : 'border-gray-600'
                  }`}
                  placeholder=""
                  required
                  disabled={isLoading}
                />
                {renderError('company')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors"
                  placeholder=""
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-3 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                    errors.email ? 'border-red-500' : 'border-gray-600'
                  }`}
                  placeholder=""
                  required
                  disabled={isLoading}
                />
                {renderError('email')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full px-4 py-3 pr-12 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                      errors.password ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder=""
                    required
                    minLength={8}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-700 rounded transition-colors"
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {renderError('password')}
                {!errors.password && (
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 8 characters with uppercase, lowercase, and number
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-3 pr-12 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder=""
                    required
                    minLength={8}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-700 rounded transition-colors"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {renderError('confirmPassword')}
              </div>

              <div className={`bg-gray-800/50 border rounded-lg p-4 ${
                errors.agreeToTerms ? 'border-red-500' : 'border-gray-700'
              }`}>
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="w-4 h-4 bg-black border border-gray-600 rounded focus:ring-2 focus:ring-white mt-0.5"
                    required
                    disabled={isLoading}
                  />
                  <span className="text-sm text-gray-300 leading-relaxed">
                    I agree to the{' '}
                    <button type="button" className="text-white hover:underline">
                      Terms of Service
                    </button>{' '}
                    and{' '}
                    <button type="button" className="text-white hover:underline">
                      Privacy Policy
                    </button>
                  </span>
                </label>
                {renderError('agreeToTerms')}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 rounded-lg font-medium transition-all duration-200 ${
                  isLoading
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-black hover:bg-gray-200 hover:shadow-lg transform hover:scale-[1.02]'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>Creating Account...</span>
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>

            {/* Switch to Login */}
            <div className="mt-6 text-center">
              <p className="text-gray-400">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={handleLoginRedirect}
                  className="text-white font-medium hover:underline transition-colors"
                  disabled={isLoading}
                >
                  Login
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 p-6 text-center">
        <p className="text-gray-500 text-sm">
          © 2025 Wellsite. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default WellsiteRegister;
